import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EGoalsLens, IGoalsCell, IGoalsColumn, IGoalsMatrix, IGoalsRow } from './campaign-details-goals.interface';
import { getGoalsPlanTotal } from './campaign-details-goals.util';

export interface IGoalsCsvFormatters {
  translate: (key: string, params?: Record<string, string | number>) => string;
}

const CSV_KEY = 'pages.campaignDetails.goals.csv';

type TCsvValue = string | number | null;

/** Text starting with one of these is run as a formula by spreadsheets; a leading `'` keeps it as text. */
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function quote(value: TCsvValue): string {
  const text = typeof value === 'string' && FORMULA_PREFIX.test(value) ? `'${value}` : String(value ?? '');

  return `"${text.replace(/"/g, '""')}"`;
}

function getFulfilled(cell: IGoalsCell): number {
  return cell.slots.filter((slot) => slot.kind === 'counted' && slot.status === EAnalyticsStatus.ok).length;
}

function isCounted(cell: IGoalsCell): boolean {
  return cell.kind === 'required' || cell.kind === 'everyStream';
}

/** Delivered, Required, Fully fulfilled, Extra (+ KPI targets reached when the group has KPI goals). */
function getCellValues(
  cell: IGoalsCell,
  column: IGoalsColumn,
  translate: IGoalsCsvFormatters['translate'],
): TCsvValue[] {
  const kpi = column.hasKpiGoals
    ? [isCounted(cell) && cell.lenses[EGoalsLens.kpis].hasGoals ? cell.lenses[EGoalsLens.kpis].done : null]
    : [];

  switch (cell.kind) {
    case 'notInPlan':
      return [translate(`${CSV_KEY}.notInPlan`), null, null, null, ...kpi.map(() => null)];
    case 'optional':
      // The matrix shows optional groups as "n delivered" only; bonus contents are already in that count.
      return [cell.optionalDelivered, translate('pages.campaignDetails.goals.frequency.optional'), null, null, ...kpi];
    default:
      return [cell.counted, cell.required, getFulfilled(cell), cell.extraSlots.length, ...kpi];
  }
}

function sum(cells: IGoalsCell[], pick: (cell: IGoalsCell) => number): number {
  return cells.reduce((total, cell) => total + pick(cell), 0);
}

/**
 * The matrix as a sheet: one line per creator (in the table's order) with plan progress for both lenses, then
 * per deliverable group Delivered / Required / Fully fulfilled / Extra (+ KPI targets reached), then an
 * "All creators" totals line. Numbers stay in their own cells so spreadsheets can sum and sort them.
 * UTF-8 BOM so Excel reads umlauts.
 */
export function buildGoalsCsv(matrix: IGoalsMatrix, rows: IGoalsRow[], formatters: IGoalsCsvFormatters): string {
  const { translate } = formatters;
  const header = [
    translate(`${CSV_KEY}.creator`),
    translate(`${CSV_KEY}.groupsComplete`),
    translate(`${CSV_KEY}.groupsInPlan`),
    translate(`${CSV_KEY}.groupsOnTarget`),
    translate(`${CSV_KEY}.groupsWithKpiGoals`),
    ...matrix.columns.flatMap((column) => [
      translate(`${CSV_KEY}.delivered`, { group: column.name }),
      translate(`${CSV_KEY}.required`, { group: column.name }),
      translate(`${CSV_KEY}.fulfilled`, { group: column.name }),
      translate(`${CSV_KEY}.extra`, { group: column.name }),
      ...(column.hasKpiGoals ? [translate(`${CSV_KEY}.kpiTargetsReached`, { group: column.name })] : []),
    ]),
  ];
  const lines: TCsvValue[][] = rows.map((row) => [
    row.creatorName,
    row.planProgress[EGoalsLens.deliverables].complete,
    row.planProgress[EGoalsLens.deliverables].total,
    row.planProgress[EGoalsLens.kpis].complete,
    row.planProgress[EGoalsLens.kpis].total,
    ...matrix.columns.flatMap((column) => getCellValues(row.cells[column.ctaId], column, translate)),
  ]);
  const deliverablesTotal = getGoalsPlanTotal(rows, EGoalsLens.deliverables);
  const kpisTotal = getGoalsPlanTotal(rows, EGoalsLens.kpis);
  const totals: TCsvValue[] = [
    translate('pages.campaignDetails.goals.footer.allCreators'),
    deliverablesTotal.complete,
    deliverablesTotal.total,
    kpisTotal.complete,
    kpisTotal.total,
    ...matrix.columns.flatMap((column) => {
      const cells = rows.map((row) => row.cells[column.ctaId]);
      const counted = cells.filter(isCounted);

      return [
        // Optional groups add to Delivered only, like their creator lines; they owe nothing, so nothing else counts them.
        sum(counted, (cell) => cell.counted) + sum(cells, (cell) => cell.optionalDelivered),
        sum(counted, (cell) => cell.required),
        sum(counted, getFulfilled),
        sum(counted, (cell) => cell.extraSlots.length),
        ...(column.hasKpiGoals
          ? [
              sum(
                counted.filter((cell) => cell.lenses[EGoalsLens.kpis].hasGoals),
                (cell) => cell.lenses[EGoalsLens.kpis].done,
              ),
            ]
          : []),
      ];
    }),
  ];

  return `\uFEFF${[header, ...lines, totals].map((line) => line.map(quote).join(',')).join('\r\n')}`;
}

export function getGoalsCsvFileName(campaignName: string, date: Date): string {
  const slug = campaignName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const day = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ];

  return `goals-${slug || 'campaign'}-${day.join('-')}.csv`;
}

export function downloadCsvFile(csv: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
